/**
 * A PDF the agent produced, shown where it was asked for.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT IS NOT A DOWNLOAD LINK
 *
 * The agent used to answer "here is a link" — and before that, "I could not
 * send it", because `send_pdf` was written for Telegram and matched nothing on
 * the web. A link is the wrong shape anyway: somebody who asks to see an
 * invoice wants to look at it, and a download that lands in a folder and opens
 * in another application has answered a question they did not ask.
 *
 * So the document renders in the conversation. Hovering lifts it, clicking
 * opens it full-size with zoom. The file is fetched with the customer's own
 * token and held as a blob URL, because the PDF routes are authorised per
 * tenant and there is no public link to any tenant's invoice — by design.
 */

import { useEffect, useRef, useState } from 'react';
import { Download, Maximize2, Minus, Plus, X } from 'lucide-react';
import { fetchPdfBlobUrl } from '@/lib/api';
import { useI18n } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';

export type AgentDocument = {
  entity_type: string;
  id: string;
  number: string;
  filename: string;
  url: string;
  bytes: number;
  /**
   * How to fetch `url`, stated by the server — see AgentDocument in
   * backend/src/agent/web.ts. Reports are GET routes and documents are POST
   * ones; the card used POST for both and every report the agent produced came
   * back as "Could not open this document".
   */
  method?: 'GET' | 'POST';
};

export function DocumentCard({ doc }: { doc: AgentDocument }) {
  const { t } = useI18n();
  const [blob, setBlob] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  /* Kept in a ref as well so the unmount cleanup revokes the CURRENT url
     rather than the one captured when the effect first ran. */
  const current = useRef<string | null>(null);

  useEffect(() => {
    let alive = true;
    /* The server states the method — reports are GET, documents POST. */
    fetchPdfBlobUrl(doc.url, undefined, false, doc.method ?? 'POST')
      .then((url) => {
        if (!alive) {
          URL.revokeObjectURL(url);
          return;
        }
        current.current = url;
        setBlob(url);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (current.current) URL.revokeObjectURL(current.current);
    };
  }, [doc.url]);

  /* Escape closes, and the arrow keys are left alone so the PDF viewer's own
     paging keeps working while the overlay is open. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
      if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(3, z + 0.25));
      if (e.key === '-') setZoom((z) => Math.max(0.5, z - 0.25));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const size =
    doc.bytes > 1024 * 1024
      ? `${(doc.bytes / 1024 / 1024).toFixed(1)} MB`
      : `${Math.max(1, Math.round(doc.bytes / 1024))} KB`;

  if (failed) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-[12px] text-destructive">
        {t('agent_doc_failed')} — {doc.filename}
      </div>
    );
  }

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOpen(true)}
        className={cn(
          'group relative w-full max-w-[280px] cursor-zoom-in overflow-hidden rounded-xl border bg-card',
          /*
            Hover REVEALS the document, it does not merely nudge it.

            The first version scaled the card 6% and left the same cropped
            strip showing, which told the reader nothing they could not
            already see — it looked like a hover effect rather than a way to
            read the page. Now the preview grows to a full page height, so
            hovering answers "what is in this?" without a click, and the click
            is for zooming and downloading.
          */
          'transition-shadow duration-200 hover:z-10 hover:shadow-xl',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <div
          className={cn(
            'pointer-events-none relative overflow-hidden bg-muted',
            /* h-[190px] is a legible thumbnail; the hover height is a full A4
               page at this width, so nothing is cut off. */
            'h-[190px] transition-[height] duration-300 ease-out group-hover:h-[396px]',
          )}
        >
          {blob ? (
            /*
              `#toolbar=0` hides the browser's own chrome so the preview reads
              as a thumbnail rather than as an embedded application. The overlay
              gets the full toolbar back.
            */
            <iframe
              src={`${blob}#toolbar=0&navpanes=0&view=FitH`}
              title={doc.filename}
              className="h-[400px] w-full border-0"
              tabIndex={-1}
            />
          ) : (
            <div className="flex h-full animate-pulse items-center justify-center text-[11px] text-muted-foreground">
              {t('agent_doc_loading')}
            </div>
          )}
          {/* The fade signals "there is more below" on the thumbnail, and gets
              out of the way once the whole page is showing. */}
          <div className="absolute inset-0 bg-gradient-to-t from-card/90 via-transparent to-transparent transition-opacity duration-300 group-hover:opacity-0" />
          <div className="absolute end-2 top-2 rounded-md bg-background/85 p-1 opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
            <Maximize2 className="size-3.5" />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
          <div className="min-w-0">
            <p className="truncate text-[12.5px] font-medium">{doc.number}</p>
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {doc.entity_type} · {size}
            </p>
          </div>
          <a
            href={blob ?? undefined}
            download={doc.filename}
            onClick={(e) => e.stopPropagation()}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            title={t('agent_doc_download')}
          >
            <Download className="size-3.5" />
          </a>
        </div>
      </div>

      {open && blob ? (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex items-center justify-between gap-3 px-4 py-2 text-white"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="truncate text-[13px] font-medium">
              {doc.number} · {doc.entity_type}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
                className="rounded-md p-1.5 hover:bg-white/15"
                aria-label="zoom out"
              >
                <Minus className="size-4" />
              </button>
              <span className="w-12 text-center text-[12px] tabular-nums">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
                className="rounded-md p-1.5 hover:bg-white/15"
                aria-label="zoom in"
              >
                <Plus className="size-4" />
              </button>
              <a
                href={blob}
                download={doc.filename}
                className="rounded-md p-1.5 hover:bg-white/15"
                aria-label="download"
              >
                <Download className="size-4" />
              </a>
              <button
                onClick={() => setOpen(false)}
                className="rounded-md p-1.5 hover:bg-white/15"
                aria-label="close"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-4" onClick={(e) => e.stopPropagation()}>
            {/*
              Scaled with a transform rather than by resizing the frame: the
              PDF renders once at full quality and the browser scales the
              raster, which stays smooth while a re-layout of an embedded
              viewer at every zoom step does not.
            */}
            <div
              className="mx-auto origin-top transition-transform duration-150"
              style={{ transform: `scale(${zoom})`, width: 'min(920px, 100%)' }}
            >
              <iframe
                src={`${blob}#view=FitH`}
                title={doc.filename}
                className="h-[80vh] w-full rounded-lg border-0 bg-white shadow-2xl"
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
